import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { XCircle, AlertTriangle, Loader2, Calendar, Clock } from 'lucide-react';
import api from '../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const BookingCancel = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_cancelled, error
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    cancelBooking();
  }, [token]);

  const cancelBooking = async () => {
    try {
      const response = await api.get(`/bookings/cancel/${token}`);
      setBooking(response.data.booking);
      
      if (response.data.already_cancelled) {
        setStatus('already_cancelled');
      } else {
        setStatus('success');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Error al cancelar la reserva');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen py-20 px-6 flex items-center justify-center" data-testid="booking-cancel-page">
      <Card className="w-full max-w-md glass-effect border-white/10">
        <CardHeader className="text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto animate-spin text-primary" />
              <CardTitle className="text-2xl font-heading uppercase mt-4">Cancelando...</CardTitle>
            </>
          )}
          
          {status === 'success' && (
            <>
              <XCircle className="h-16 w-16 mx-auto text-orange-500" />
              <CardTitle className="text-2xl font-heading uppercase mt-4 text-orange-500">Reserva Cancelada</CardTitle>
            </>
          )}
          
          {status === 'already_cancelled' && (
            <>
              <AlertTriangle className="h-16 w-16 mx-auto text-yellow-500" />
              <CardTitle className="text-2xl font-heading uppercase mt-4 text-yellow-500">Ya Cancelada</CardTitle>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 mx-auto text-red-500" />
              <CardTitle className="text-2xl font-heading uppercase mt-4 text-red-500">Error</CardTitle>
            </>
          )}
        </CardHeader>
        
        <CardContent className="text-center">
          {status === 'success' && booking && (
            <div className="space-y-4">
              <p className="text-muted-foreground">Tu reserva ha sido cancelada. El horario queda disponible para otros usuarios.</p>
              
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mt-4">
                <p className="text-sm text-muted-foreground mb-2">Reserva cancelada:</p>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-orange-500" />
                  <span className="font-semibold line-through opacity-70">{formatDate(booking.date)}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-5 w-5 text-orange-500" />
                  <span className="font-semibold line-through opacity-70">{booking.time_slot} hs</span>
                </div>
              </div>
            </div>
          )}
          
          {status === 'already_cancelled' && booking && (
            <div className="space-y-4">
              <p className="text-muted-foreground">Esta reserva ya había sido cancelada anteriormente.</p>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-yellow-500" />
                  <span className="font-semibold line-through opacity-70">{formatDate(booking.date)}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-500" />
                  <span className="font-semibold line-through opacity-70">{booking.time_slot} hs</span>
                </div>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <p className="text-muted-foreground">{error}</p>
          )}
          
          <div className="mt-6 space-y-3">
            <Link to="/booking">
              <Button className="w-full bg-primary hover:bg-primary/90 font-bold uppercase" data-testid="new-booking">
                Hacer Nueva Reserva
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" className="w-full border-white/20 hover:bg-white/5" data-testid="go-to-dashboard">
                Ir al Dashboard
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
